// The Overview edit buffer, owned by the detail pane rather than by the tab.
//
// If the tab owned it, switching to Media and back would silently throw away
// everything typed -- the same class of quiet data loss as switching video
// mid-edit. Living one level up means the buffer survives tab changes and the
// pane can refuse to move on while it is dirty.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeError } from './api';
import { FIELDS } from './schema';
import { parseProducts, readBool, readOptOut, serializeProducts, writeBool } from '../ps/format';

// The record holds strings; the form holds the shapes the inputs want. These
// two functions are the only place that translation happens.
export function toForm(video) {
  const form = {};
  FIELDS.forEach((field) => {
    const raw = video?.[field.key];
    if (field.type === 'flag') form[field.key] = readBool(raw);
    else if (field.type === 'optout') form[field.key] = readOptOut(raw);
    else if (field.type === 'products') form[field.key] = parseProducts(raw);
    else form[field.key] = raw == null ? '' : String(raw);
  });
  return form;
}

export function toPatch(form) {
  const patch = {};
  FIELDS.forEach((field) => {
    const value = form[field.key];
    // Flags must be written as 'true'/'false' strings: Home.js compares them
    // with === against a string, so a real boolean reads as not-live forever.
    if (field.type === 'flag' || field.type === 'optout') patch[field.key] = writeBool(value);
    else if (field.type === 'products') patch[field.key] = serializeProducts(value);
    else patch[field.key] = value;
  });
  return patch;
}

export function useVideoForm({ identifier, video, patch, toast }) {
  const baseline = useMemo(() => toForm(video), [video]);
  const [form, setForm] = useState(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // A different video, or a save landing, resets the buffer. The guard against
  // losing edits lives in the shell, before the selection changes.
  useEffect(() => {
    setForm(baseline);
    setError('');
  }, [baseline, identifier]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  const set = useCallback((key, value) => setForm((prev) => ({ ...prev, [key]: value })), []);
  const revert = useCallback(() => setForm(baseline), [baseline]);

  const save = useCallback(async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setError('');
    try {
      await patch(identifier, toPatch(form));
      toast('Saved.', 'success');
    } catch (err) {
      setError(describeError(err, 'Nothing was saved.'));
    } finally {
      setSaving(false);
    }
  }, [form, identifier, isDirty, patch, saving, toast]);

  return { form, set, revert, save, saving, error, isDirty };
}

export default useVideoForm;
